import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import MobileReadMore, { useMobileReadMore } from "mobile/components/MobileReadMore";

/**
 * Line-clamped text with a hover tooltip that reveals the full string.
 * Used by:
 *   • Medicines table + Medicine trend audit (2-line clamp, default)
 *   • Hospitalisation history — Description (4-line clamp)
 *   • Chief-complaints active + past + trend audit (3-line clamp)
 *
 * The popover is PORTALLED to `document.body` with `position: fixed` so
 * it escapes every ancestor `overflow: hidden` / `overflow-x: auto` clip
 * — this is the same recipe the Diagnosis section's `BulletListCell` uses
 * to survive the CC past card's `.cc3-card { overflow: hidden }` (the
 * inline anchor version was getting clipped mid-tooltip there).
 *
 * The popover appears ONLY when the clamp is actually eliding text
 * (`scrollHeight > clientHeight`). Fully-visible strings get no reveal.
 */

const POP_MAX_W = 380;
const POP_MIN_W = 240;
const POP_MAX_H = 240;
const MIN_READABLE_H = 60;
const PAD = 8;
const ABOVE_GAP = 12;
const BELOW_GAP = 20;

const MetaTooltip = ({ text, lines }) => {
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);
  const [popStyle, setPopStyle] = useState(null);
  const clampRef = useRef(null);
  const anchorRef = useRef(null);

  // Measure after every render so a text change or container resize
  // recomputes the truncated flag. Layout-effect so we don't flash the
  // popover on the frame before the check runs.
  useLayoutEffect(() => {
    const node = clampRef.current;
    if (!node) {
      setTruncated(false);
      return;
    }
    // scrollHeight = natural content height ignoring the -webkit-box
    // clamp. clientHeight = clipped visible height. When the former
    // exceeds the latter, the multi-line clamp is cutting rows off.
    //
    // scrollWidth > clientWidth catches the horizontal-ellipsis case —
    // Past Medicines' Description cell, for example, inherits
    // white-space:nowrap from `.med3-cell` and renders a single-line
    // ellipsis; the vertical check would never fire there.
    const vertOverflow = node.scrollHeight - node.clientHeight > 1;
    const horzOverflow = node.scrollWidth - node.clientWidth > 1;
    setTruncated(vertOverflow || horzOverflow);
  }, [text, lines]);
  /* MOBILE ONLY: a 5-line cut with "Read more" instead of a hover reveal.
     The anchor and clamp classes stay for their type and colour; the cut is
     MobileReadMore's. */
  const readMore = useMobileReadMore();

  if (!text) return "—";
  if (readMore) {
    return (
      <span className="med3-meta-anchor">
        <MobileReadMore textClassName="med3-meta-clamp">{text}</MobileReadMore>
      </span>
    );
  }

  // Position the portalled popover in viewport coordinates. Called on
  // pointer enter and each move so it tracks the cursor. Anchored on
  // top OR bottom (never both) so the pill's edge stays glued to the
  // cursor line regardless of its rendered height — nothing about the
  // popover's own size ever feeds this math, so a stale first-frame
  // measurement can't produce a mis-clamp.
  const place = (e) => {
    if (!truncated) return;
    const availBelow = Math.max(0, window.innerHeight - (e.clientY + BELOW_GAP) - PAD);
    const availAbove = Math.max(0, e.clientY - ABOVE_GAP - PAD);
    const openAbove = availAbove > availBelow;
    const avail = openAbove ? availAbove : availBelow;
    const maxHeight = Math.max(MIN_READABLE_H, Math.min(POP_MAX_H, avail));

    // Horizontal — start at cursor + 12 (readable, not under the
    // pointer), clamp both edges to the viewport with an 8px pad.
    let left = e.clientX + 12;
    if (left + POP_MAX_W + PAD > window.innerWidth) {
      left = window.innerWidth - POP_MAX_W - PAD;
    }
    if (left < PAD) left = PAD;

    setPopStyle({
      position: "fixed",
      left: `${left}px`,
      top: openAbove ? "auto" : `${e.clientY + BELOW_GAP}px`,
      bottom: openAbove ? `${window.innerHeight - (e.clientY - ABOVE_GAP)}px` : "auto",
      right: "auto",
      minWidth: `${POP_MIN_W}px`,
      maxWidth: `${POP_MAX_W}px`,
      maxHeight: `${maxHeight}px`,
    });
  };

  const show = (e) => {
    if (!truncated) return;
    place(e);
    setOpen(true);
  };
  const move = (e) => {
    if (!open) return;
    place(e);
  };
  const hide = () => setOpen(false);

  return (
    <span
      ref={anchorRef}
      className={`med3-meta-anchor${truncated ? " is-truncated" : ""}`}
      onMouseEnter={show}
      onMouseMove={move}
      onMouseLeave={hide}
    >
      <span
        ref={clampRef}
        className="med3-meta-clamp"
        style={lines ? { WebkitLineClamp: lines, lineClamp: lines } : undefined}
      >
        {text}
      </span>
      {open && popStyle && ReactDOM.createPortal(
        <div className="med3-meta-pop" role="tooltip" style={popStyle}>
          {text}
        </div>,
        document.body,
      )}
    </span>
  );
};
MetaTooltip.propTypes = {
  text: PropTypes.string,
  /** Override the default 2-line clamp. Only applied when set. */
  lines: PropTypes.number,
};

export default MetaTooltip;
